import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Sparkles, X } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { FormValues } from "./form-schema";

// Use the correct Annotation type
export type Annotation = {
  type: string;
  end_index: number;
  start_index: number;
  title: string;
  url: string;
};

interface CompanyDescriptionProps {
  isGeneratingDescription?: boolean;
  annotations?: Annotation[];
  onAnnotationClick?: (annotation: Annotation) => void;
  onSkipAI?: () => void;
}

export function CompanyDescription({
  isGeneratingDescription = false,
  annotations = [],
  onAnnotationClick,
  onSkipAI,
}: CompanyDescriptionProps) {
  const { t } = useTranslation();

  const form = useFormContext<FormValues>();

  // Filter out exact duplicates for URLs
  const uniqueAnnotations = annotations.filter(
    (annotation, index, self) =>
      index === self.findIndex((a) => a.url === annotation.url),
  );

  const handleAnnotationClick = (annotation: Annotation) => {
    // Open URL in new tab
    window.open(annotation.url, "_blank", "noopener,noreferrer");

    // Also call the original onAnnotationClick if provided
    if (onAnnotationClick) {
      onAnnotationClick(annotation);
    }
  };

  const handleSkipAI = () => {
    onSkipAI?.();
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold mb-6">
          {t("mandanten_create.company_description.title")}
        </h2>
        <FormField
          control={form.control}
          name="companyDescription"
          render={({ field }) => (
            <FormItem>
              <FormDescription className="text-sm text-gray-600">
                {isGeneratingDescription
                  ? t(
                      "mandanten_create.company_description.generating_description",
                    )
                  : t("mandanten_create.company_description.description_help")}
              </FormDescription>
              <FormControl>
                <div className="relative">
                  <Textarea
                    {...field}
                    disabled={isGeneratingDescription}
                    placeholder={
                      isGeneratingDescription
                        ? t(
                            "mandanten_create.company_description.generating_placeholder",
                          )
                        : "Kaffeeklatsch GmbH betreibt ein gemütliches Café und bietet täglich frisch gerösteten Kaffee, hausgemachte Kuchen, warme und kalte Speisen sowie eine große Auswahl an Tees und alkoholfreien Getränken an. Das Unternehmen serviert Frühstück, Mittagessen und Snacks für Laufkundschaft sowie Geschäftskunden und verfügt über eine vollständige Küche für die Zubereitung von Sandwiches, Salaten, Suppen und warmen Hauptgerichten. Zusätzlich zum Café-Betrieb bietet Kaffeeklatsch GmbH professionelle Catering-Dienstleistungen für Firmenveranstaltungen, private Feiern und Konferenzen an, einschließlich Lieferung, Aufbau und Service vor Ort."
                    }
                    className="min-h-[280px] w-full"
                  />
                  {isGeneratingDescription && (
                    <div className="absolute top-2 right-2 flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleSkipAI}
                        className="h-7 px-2 text-xs"
                      >
                        <X className="h-3 w-3 mr-1" />
                        {t("mandanten_create.company_description.skip_ai")}
                      </Button>
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                  {!isGeneratingDescription && field.value && (
                    <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-gray-50 text-gray-700 px-2 py-1 rounded-md text-xs font-medium border border-gray-200">
                      <Sparkles className="h-3 w-3" />
                      <span>
                        {t(
                          "mandanten_create.company_description.ai_suggestion",
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {uniqueAnnotations && uniqueAnnotations.length > 0 && (
          <div className="mt-6">
            <Dialog>
              <DialogTrigger asChild>
                <button className="flex items-center gap-3 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors cursor-pointer group rounded-lg px-3 py-2">
                  <div className="flex -space-x-2">
                    {uniqueAnnotations.slice(0, 3).map((ann, idx) => (
                      <div
                        key={idx}
                        className="w-5 h-5 rounded-full flex items-center justify-center border-2 border-white relative bg-white"
                        style={{ zIndex: 30 - idx * 10 }}
                      >
                        <img
                          src={`https://s2.googleusercontent.com/s2/favicons?domain_url=${ann.url}`}
                          alt={ann.title}
                          className="w-3 h-3"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = "none";
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <span className="font-medium">
                    {t("mandanten_create.company_description.sources")}
                  </span>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl z-[9999]">
                <DialogHeader>
                  <DialogTitle>
                    {t("mandanten_create.company_description.sources")}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 gap-4">
                    {uniqueAnnotations.map((ann, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-50 rounded-lg p-4 cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                        onClick={() => handleAnnotationClick(ann)}
                      >
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center bg-white">
                              <img
                                src={`https://s2.googleusercontent.com/s2/favicons?domain_url=${ann.url}`}
                                alt={ann.title}
                                className="w-3 h-3"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = "none";
                                }}
                              />
                            </div>
                            <div className="text-[13px] font-medium text-gray-900">
                              {ann.title}
                            </div>
                          </div>
                          <div className="text-[13px] font-medium text-gray-600 line-clamp-2">
                            {ann.url}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
    </div>
  );
}
